import { Close, ContentPaste, KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useNotifications } from 'components/notify';
import useConfirm from 'hooks/useConfirm';
import { useClipboard } from 'providers/ClipboardProvider';
import React, { useState } from 'react';
import { useSWRConfig } from 'swr';

const ClipboardUI: React.FC = () => {
  const { copiedEntities, removeFromClipboard, clearClipboard } =
    useClipboard();
  const { deleteEntity } = useEntityActions();
  const { showNotification } = useNotifications();
  const { confirm } = useConfirm();
  const workspaceId = useWorkspaceId();
  const { mutate, cache } = useSWRConfig();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAll = async () => {
    const confirmed = await confirm(
      `Are you sure you want to delete all ${copiedEntities.length} entities? This action cannot be undone.`,
      'Delete All Entities',
    );

    if (!confirmed) return;

    setIsDeleting(true);
    showNotification('Deleting entities...', { loading: true });

    try {
      // Delete each entity one by one
      for (const entity of copiedEntities) {
        try {
          const response = await deleteEntity(entity.id);
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.message || `Failed to delete ${entity.name}`,
            );
          }
        } catch (error) {
          console.error(`Error deleting entity ${entity.id}:`, error);
          showNotification(
            `Failed to delete ${entity.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            { variant: 'error' },
          );
        }
      }

      // Invalidate relevant caches
      Array.from(cache.keys())
        .filter(
          (x) =>
            x.includes(`/workspace/${workspaceId}/entity`) ||
            x.includes(`/workspace/${workspaceId}/entities`),
        )
        .forEach((key) => mutate(key));

      // Clear clipboard after deletion
      clearClipboard();

      showNotification('All entities deleted successfully', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error during batch delete:', error);
      showNotification('Some entities could not be deleted', {
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (copiedEntities.length === 0) {
    return null;
  }

  return (
    <Card
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        maxWidth: 300,
        maxHeight: 400,
        overflow: 'auto',
        zIndex: 1000,
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <ContentPaste fontSize="small" color="primary" />
            <Typography>Clipboard</Typography>
          </Stack>
        }
        action={
          <IconButton size="small" onClick={clearClipboard}>
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <CardContent
        sx={{
          p: 0,
        }}
      >
        <Accordion
          variant="outlined"
          sx={{
            border: 0,
            background: 'transparent',
          }}
          onChange={(_, expanded) => setIsExpanded(expanded)}
        >
          <AccordionSummary expandIcon={<KeyboardArrowDown />}>
            {copiedEntities.length} entities in clipboard
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 0,
            }}
          >
            <List>
              {copiedEntities.map((entity) => (
                <ListItem key={entity.id}>
                  <ListItemText
                    sx={{
                      span: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      },
                      whiteSpace: 'nowrap',
                    }}
                    primary={entity.name}
                    secondary={entity.types[0]}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={() => removeFromClipboard(entity.id)}
                    >
                      <Close />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {isExpanded && copiedEntities.length > 0 && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                sx={{ p: 2, pt: 0 }}
              >
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={handleDeleteAll}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? 'Deleting...'
                    : `Delete All Entities (${copiedEntities.length})`}
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default ClipboardUI;
